import Add from '@mui/icons-material/Add'
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from '@mui/material'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { CustomInput, CustomSelect, selectTypes } from 'src/consumer/components/helperComponents'
import { currency_list, FEATURE_BUNDLE_TYPES_ARRAY } from 'src/consumer/constants'
import { UnsecureSetupContext } from 'src/consumer/context/UnsecureSetupContext'
import { checkAndGetTargetValue, genericHandleChange } from 'src/consumer/helpers/utilFunctions'
import { sleep } from 'src/mentor/helpers/utilityFunctions'
import { postMPDataWithoutParams } from 'src/mentor/services'
import {
  StyledEventInputContainer,
  StyledEventReactSelect,
} from '../../MentorAvailabilityPage/MentorAvailabilityPageStyles'
import { SavingCircularProgress } from '../../MentorProfilePage/MentorProfilePageStyles'
import {
  createSubmittablePricingMatrix,
  filterOtherSelectedCountries,
  getUsablePricingMatrix,
  validatePricingMatrixToSave,
} from '../MentorEventsHelpers'
import {
  AddCustomPriceButton,
  AddCustomPriceSubHeading,
  AddPricingFab,
  CancelAddCustomPrice,
  CancelConfirmationButton,
  ChangesWillNotBeSavedButton,
  CountryPricingContainer,
  SavePlanButton,
} from '../MentorEventsPageStyles'
import BundlePackCustomPriceComponent from './bundle/BundlePackCustomPriceComponent'

const LEGACY_BUNDLE_DEFAULT_VALIDITY_DAYS = 30
const COUNT_BASED_BUNDLE_TYPE_ID = FEATURE_BUNDLE_TYPES_ARRAY?.[0]?.id || 'count_based'

function getLegacyBundleValidityInDays(bundlePack) {
  const bundleItems = Array.isArray(bundlePack?.bundle) ? bundlePack.bundle : []
  const firstRowWithValidity = bundleItems.find(
    (bundleItem) => Number(bundleItem?.featureAndCreditValidityInDays) > 0,
  )
  const candidateValidity =
    firstRowWithValidity?.featureAndCreditValidityInDays ??
    bundlePack?.bundleValidityInDays ??
    bundlePack?.validity ??
    bundlePack?.expiryInDays ??
    LEGACY_BUNDLE_DEFAULT_VALIDITY_DAYS

  const parsedValidity = Number(candidateValidity)
  return Number.isFinite(parsedValidity) && parsedValidity > 0
    ? parsedValidity
    : LEGACY_BUNDLE_DEFAULT_VALIDITY_DAYS
}

function buildDefaultLegacyBundleItem(validityInDays = LEGACY_BUNDLE_DEFAULT_VALIDITY_DAYS) {
  return {
    type: COUNT_BASED_BUNDLE_TYPE_ID,
    maxCount: 1,
    featureAndCreditValidityInDays: validityInDays,
  }
}

function normalizeLegacyBundleItems(bundleItems, validityInDays) {
  const normalizedValidity = Number(validityInDays)
  const safeValidity = Number.isFinite(normalizedValidity) && normalizedValidity > 0
    ? normalizedValidity
    : LEGACY_BUNDLE_DEFAULT_VALIDITY_DAYS
  const rawItems =
    Array.isArray(bundleItems) && bundleItems.length > 0
      ? bundleItems
      : [buildDefaultLegacyBundleItem(safeValidity)]

  return rawItems.map((bundleItem) => ({
    ...bundleItem,
    type: COUNT_BASED_BUNDLE_TYPE_ID,
    maxCount: Number(bundleItem?.maxCount ?? 0),
    featureAndCreditValidityInDays: safeValidity,
  }))
}

export default function BaseAddCustomPriceComponent({
  selectedPlan,
  setSelectedPlan,
  handleChangePlan: changePlan,
  closeFunction,
  refreshOneOnOnePlans,
  type = 'default',
  allOneOnOnePlans,
  saveOneOnOnePlanApi = `/api/secure/teacher/1on1-custom-plan`,
  saveBundlePackApi = `/api/secure/teacher/1on1-custom-bundle/save`,
  mentorProfileData,
  preselectedPlanId,
  onSaveSuccess,
  bundleEditorMode = 'session',
  bundlePlanOptions,
  saveBundlePackRequestFn,
}) {
  const { unSecureSetupData } = useContext(UnsecureSetupContext)
  const [pricingMatrix, setPricingMatrix] = useState()
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [confirmationType, setConfirmationType] = useState('')
  const [subscriptionPlanToUse, setSubscriptionPlanToUse] = useState()
  const dialogContentRef = useRef(null)
  const [saving, setSaving] = useState(false)
  const [changesMade, setChangesMade] = useState(false)
  const [justRendered, setJustRenderded] = useState(true)

  const isBundle = type === 'bundle'
  const isLegacyBundle = isBundle && bundleEditorMode === 'legacy'
  const availableBundlePlanOptions = bundlePlanOptions || allOneOnOnePlans || []

  const planIsValid = useMemo(() => {
    const hasValidPricing = validatePricingMatrixToSave(pricingMatrix)
    if (!isBundle) {
      return hasValidPricing
    }

    const bundleItems = Array.isArray(subscriptionPlanToUse?.bundle) ? subscriptionPlanToUse.bundle : []
    const hasValidBundleItems =
      bundleItems.length > 0 &&
      bundleItems.every((bundleItem) => {
        if (!bundleItem?.pricingPackId) {
          return false
        }
        if (isLegacyBundle) {
          return Number(bundleItem?.maxCount) > 0
        }
        if (bundleItem?.type === FEATURE_BUNDLE_TYPES_ARRAY?.[1]?.id) {
          return Number(bundleItem?.featureAndCreditValidityInDays) > 0
        }
        return Number(bundleItem?.maxCount) > 0
      })

    if (isLegacyBundle) {
      return (
        hasValidPricing &&
        Boolean(subscriptionPlanToUse?.title) &&
        Number(subscriptionPlanToUse?.bundleValidityInDays) > 0 &&
        hasValidBundleItems
      )
    }

    return (
      hasValidPricing &&
      Boolean(subscriptionPlanToUse?.title) &&
      Number(subscriptionPlanToUse?.sessionCount) > 0 &&
      hasValidBundleItems
    )
  }, [pricingMatrix, subscriptionPlanToUse, isBundle, isLegacyBundle])

  function handleChange(e, idx) {
    setChangesMade(true)
    setPricingMatrix((prev) => {
      const newMatrix = [...prev]?.map((item, index) => {
        return index === idx ? { ...item, [e.target.id]: checkAndGetTargetValue(e) } : item
      })
      return newMatrix
    })
  }

  function handleChangeSubscriptionPlanToUse(eve) {
    setChangesMade(true)
    genericHandleChange(eve, setSubscriptionPlanToUse)
  }

  function handleAddToPricingMatrix() {
    setChangesMade(true)
    setPricingMatrix((prev) => {
      const fallbackCountryCode = mentorProfileData?.location?.countryCode || mentorProfileData?.countryCode
      const foundCountryFromMatrix = prev?.find((prv) => prv?.countryId === fallbackCountryCode)
      const foundCountry = unSecureSetupData?.metaCountries?.find(
        (ctry) => ctry?.id === fallbackCountryCode,
      )
      return [
        ...(prev || []),
        {
          countryId: foundCountryFromMatrix ? '' : fallbackCountryCode || '',
          currencyId: foundCountryFromMatrix ? '' : foundCountry?.currency || 'usd',
        },
      ]
    })
  }

  function handleDelete(idx) {
    setChangesMade(true)
    setPricingMatrix((prev) => {
      return [...prev].filter((item, index) => index !== idx)
    })
  }

  function handleCloseButton() {
    if (changesMade) {
      setShowConfirmation(true)
      setConfirmationType('close')
    } else {
      closeFunction()
    }
  }

  function handleSaveButton() {
    setShowConfirmation(true)
    setConfirmationType('save')
  }

  async function SavePlan() {
    setSaving(true)

    const pricingMatrixToSave = createSubmittablePricingMatrix(pricingMatrix)
    let response

    if (isBundle) {
      const { bundleValidityInDays, ...bundlePackDraft } = subscriptionPlanToUse || {}
      const normalizedLegacyBundleItems = isLegacyBundle
        ? normalizeLegacyBundleItems(bundlePackDraft?.bundle, bundleValidityInDays)
        : Array.isArray(bundlePackDraft?.bundle)
          ? bundlePackDraft.bundle
          : []
      const bundlePackToSave = {
        ...(bundlePackDraft || {}),
        apiParam: 'plan-bundle-pack',
        pricingMatrix: pricingMatrixToSave,
        bundle: normalizedLegacyBundleItems,
        packKind: isLegacyBundle
          ? 'legacy'
          : bundlePackDraft?.packKind || 'session',
        eligibleSubscriptionPlanId: isLegacyBundle
          ? ''
          : bundlePackDraft?.eligibleSubscriptionPlanId || preselectedPlanId || '',
        sessionCount: isLegacyBundle
          ? undefined
          : Number(bundlePackDraft?.sessionCount || 0),
        expiryInDays:
          !isLegacyBundle &&
          bundlePackDraft?.expiryInDays !== '' &&
          bundlePackDraft?.expiryInDays != null
            ? Number(bundlePackDraft?.expiryInDays)
            : undefined,
        eligibleAppointmentEventTypeId: isLegacyBundle
          ? ''
          : bundlePackDraft?.eligibleAppointmentEventTypeId,
      }

      if (saveBundlePackRequestFn) {
        response = await saveBundlePackRequestFn(bundlePackToSave)
      } else {
        response = await postMPDataWithoutParams(saveBundlePackApi, bundlePackToSave)
      }
    } else {
      let toSaveApi = saveOneOnOnePlanApi
      if (toSaveApi.includes('?')) {
        if (subscriptionPlanToUse?.id) {
          toSaveApi = `${saveOneOnOnePlanApi}&planId=${subscriptionPlanToUse?.id}`
        }
      } else if (subscriptionPlanToUse?.uid || subscriptionPlanToUse?.id) {
        toSaveApi = `${saveOneOnOnePlanApi}?planId=${
          subscriptionPlanToUse?.uid || subscriptionPlanToUse?.id
        }`
      }

      response = await postMPDataWithoutParams(toSaveApi, pricingMatrixToSave)
    }

    if (response?.status === 200) {
      await refreshOneOnOnePlans?.()
      setShowConfirmation(false)
      const savedPlan =
        response?.data?.data?.bundlePack || response?.data?.data?.subscriptionPlan
      if (!isBundle) {
        changePlan?.({ target: { value: savedPlan?.id } })
      } else {
        setSelectedPlan?.(savedPlan)
      }
      onSaveSuccess?.(savedPlan)
      closeFunction()
    }
    setSaving(false)
  }

  async function Setup() {
    const fallbackCountryCode = mentorProfileData?.location?.countryCode || mentorProfileData?.countryCode
    const defaultCountry = unSecureSetupData?.metaCountries?.find(
      (ctry) => ctry?.id === fallbackCountryCode,
    )
    let pricingMatrixToSet = [{ countryId: 'row', currencyId: defaultCountry?.currency || 'usd' }]

    if (selectedPlan) {
      pricingMatrixToSet = getUsablePricingMatrix(selectedPlan?.pricingMatrix, defaultCountry?.currency)
    }

    setPricingMatrix(pricingMatrixToSet)
    const legacyBundleValidityInDays = getLegacyBundleValidityInDays(selectedPlan)

    setSubscriptionPlanToUse(
      selectedPlan
        ? {
            ...selectedPlan,
            bundle: isLegacyBundle
              ? normalizeLegacyBundleItems(selectedPlan?.bundle, legacyBundleValidityInDays)
              : Array.isArray(selectedPlan?.bundle)
                ? selectedPlan.bundle
                : [buildDefaultLegacyBundleItem(legacyBundleValidityInDays)],
            bundleValidityInDays: isLegacyBundle
              ? legacyBundleValidityInDays
              : selectedPlan?.bundleValidityInDays,
            packKind: isLegacyBundle ? 'legacy' : selectedPlan?.packKind || 'session',
            eligibleSubscriptionPlanId: isLegacyBundle
              ? ''
              : selectedPlan?.eligibleSubscriptionPlanId || preselectedPlanId || '',
            sessionCount: isLegacyBundle ? undefined : selectedPlan?.sessionCount ?? 5,
            expiryInDays: isLegacyBundle ? undefined : selectedPlan?.expiryInDays ?? 30,
          }
        : {
            title: '',
            bundle: isLegacyBundle
              ? [buildDefaultLegacyBundleItem(LEGACY_BUNDLE_DEFAULT_VALIDITY_DAYS)]
              : [{ type: COUNT_BASED_BUNDLE_TYPE_ID, maxCount: 1 }],
            bundleValidityInDays: isLegacyBundle
              ? LEGACY_BUNDLE_DEFAULT_VALIDITY_DAYS
              : undefined,
            packKind: isLegacyBundle ? 'legacy' : 'session',
            eligibleSubscriptionPlanId: isLegacyBundle ? '' : preselectedPlanId || '',
            sessionCount: isLegacyBundle ? undefined : 5,
            expiryInDays: isLegacyBundle ? undefined : 30,
          },
    )
    await sleep(400)
    setJustRenderded(false)
  }

  function showModalTitle() {
    if (isBundle) {
      if (isLegacyBundle) {
        return subscriptionPlanToUse?.id ? 'Edit Booking Pack' : 'Create Booking Pack'
      }
      return subscriptionPlanToUse?.id ? 'Edit Bundle Pack' : 'Add Bundle Pack'
    }
    return subscriptionPlanToUse?.id ? 'Edit Plan Prices' : 'Add Custom Price'
  }

  function showModalSubtitle() {
    if (isLegacyBundle) {
      return 'Choose the appointment plan, how many sessions are included, how long the pack stays valid, and the discounted price the user pays.'
    }
    if (isBundle) {
      return 'Create a bundle and add prices in different countries.'
    }
    return 'Add prices for your plan in different countries.'
  }

  useEffect(() => {
    Setup()
  }, [selectedPlan, isLegacyBundle])

  return (
    <>
      <DialogTitle>
        <span>{showModalTitle()}</span>
        <AddCustomPriceSubHeading>{showModalSubtitle()}</AddCustomPriceSubHeading>
      </DialogTitle>
      <DialogContent
        sx={{
          fontFamily: 'Outfit',
          gap: '10px',
          display: 'flex',
          flexDirection: 'column',
          paddingTop: '20px !important',
        }}
        ref={justRendered ? null : dialogContentRef}
      >
        <CustomInput
          id="title"
          label={
            isLegacyBundle
              ? 'Booking Pack Title'
              : isBundle
                ? 'Bundle Pack Title'
                : 'Subscription Title'
          }
          value={subscriptionPlanToUse?.title}
          onChange={handleChangeSubscriptionPlanToUse}
          invalid={!subscriptionPlanToUse?.title}
          required
        />
        {isLegacyBundle && (
          <>
            <CustomInput
              id="bundleValidityInDays"
              label="Pack Valid For (Days)"
              subLabel="For how many days after purchase can the user keep booking from this pack?"
              type="number"
              min={1}
              value={subscriptionPlanToUse?.bundleValidityInDays ?? ''}
              onChange={handleChangeSubscriptionPlanToUse}
              invalid={!Number(subscriptionPlanToUse?.bundleValidityInDays)}
              required
            />
            <Box
              sx={{
                border: '1px solid #d8e2f0',
                borderRadius: '12px',
                background: '#f8fbff',
                color: '#3c4a61',
                px: 2,
                py: 1.5,
                fontSize: '0.95rem',
                lineHeight: 1.6,
              }}
            >
              Add one or more appointment plans below. Each row tells us which appointment pricing
              plan this pack redeems against and how many bookings of that plan are included.
            </Box>
          </>
        )}
        {isBundle && !isLegacyBundle && (
          <>
            <CustomSelect
              id="eligibleSubscriptionPlanId"
              label="Linked Session Plan"
              options={availableBundlePlanOptions}
              value={subscriptionPlanToUse?.eligibleSubscriptionPlanId || preselectedPlanId || ''}
              onChangeEvent={(eve) =>
                handleChangeSubscriptionPlanToUse({
                  target: { id: 'eligibleSubscriptionPlanId', value: eve?.target?.value },
                })
              }
              getOptionLabel={(option) => option?.title}
              getOptionValue={(option) => option?.id}
              selectType={selectTypes.string}
            />
            <CustomInput
              id="sessionCount"
              label="Number of Sessions"
              type="number"
              value={subscriptionPlanToUse?.sessionCount ?? ''}
              onChange={handleChangeSubscriptionPlanToUse}
              invalid={!subscriptionPlanToUse?.sessionCount}
              required
            />
            <CustomInput
              id="expiryInDays"
              label="Expiry (Days)"
              type="number"
              value={subscriptionPlanToUse?.expiryInDays ?? ''}
              onChange={handleChangeSubscriptionPlanToUse}
            />
          </>
        )}
        {isBundle && (
          <Grid item xs={12}>
            <BundlePackCustomPriceComponent
              bundle={subscriptionPlanToUse?.bundle || []}
              setSelectedPlan={setSubscriptionPlanToUse}
              allOneOnOnePlans={availableBundlePlanOptions}
              bundleEditorMode={bundleEditorMode}
            />
          </Grid>
        )}
        {pricingMatrix?.map((pricingData, idx) => (
          <ChangeCountryPricing
            key={`${pricingData?.countryId}-${pricingData?.currencyId}-${idx}`}
            handleChange={(e) => handleChange(e, idx)}
            handleDelete={() => handleDelete(idx)}
            priceObject={pricingData}
            country={pricingData?.countryId}
            pricingMatrix={pricingMatrix}
            dialogContentRef={dialogContentRef}
          />
        ))}
        <div
          style={{
            position: 'sticky',
            bottom: '10px',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <AddPricingFab onClick={handleAddToPricingMatrix}>
            <Add />
          </AddPricingFab>
        </div>
      </DialogContent>
      <DialogActions>
        <CancelAddCustomPrice onClick={handleCloseButton}>Cancel</CancelAddCustomPrice>
        <AddCustomPriceButton disabled={!planIsValid} onClick={handleSaveButton}>
          {isLegacyBundle ? 'Save Booking Pack' : isBundle ? 'Save Bundle Pack' : 'Save Pricing Details'}
        </AddCustomPriceButton>
      </DialogActions>
      <Dialog open={showConfirmation} onClose={() => setShowConfirmation(false)}>
        <DialogTitle>
          {confirmationType === 'save'
            ? `Do you want to save this ${
                isLegacyBundle ? 'booking pack' : isBundle ? 'bundle pack' : 'one on one plan'
              }?`
            : 'You will lose all your changes do you want to continue?'}
        </DialogTitle>
        <DialogActions>
          <CancelConfirmationButton disabled={saving} onClick={() => setShowConfirmation(false)}>
            Cancel
          </CancelConfirmationButton>
          {confirmationType === 'save' ? (
            <Box sx={{ m: 1, position: 'relative' }}>
              <SavePlanButton
                disabled={saving}
                onClick={() => {
                  if (planIsValid) {
                    SavePlan()
                  }
                }}
              >
                {saving && (
                  <SavingCircularProgress
                    size={24}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-12px',
                      marginLeft: '-12px',
                    }}
                  />
                )}
                Save
              </SavePlanButton>
            </Box>
          ) : (
            <ChangesWillNotBeSavedButton
              onClick={() => {
                setShowConfirmation(false)
                closeFunction()
              }}
            >
              Continue
            </ChangesWillNotBeSavedButton>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}

function ChangeCountryPricing({
  priceObject,
  handleChange,
  pricingMatrix,
  handleDelete,
  dialogContentRef,
}) {
  const { unSecureSetupData } = useContext(UnsecureSetupContext)
  const isRestOfWorld = priceObject?.countryId === 'row'
  const [showConfirmation, setShowConfirmation] = useState(false)

  function changeCountry(selectValue) {
    const countryCode = selectValue?.id
    const currencyCode = selectValue?.currency
    handleChange({ target: { value: countryCode, id: 'countryId' } })
    handleChange({ target: { value: currencyCode, id: 'currencyId' } })
  }

  function deletePrice() {
    setShowConfirmation(false)
    handleDelete()
  }

  useEffect(() => {
    const dialogContent = dialogContentRef?.current
    if (dialogContent) {
      dialogContent.scrollTo({
        top: dialogContent.scrollHeight,
        behavior: 'smooth',
      })
    }
  }, [])

  return (
    <div
      style={{
        border: '1px solid #00000033',
        borderRadius: '7px',
        padding: '20px',
        boxShadow: isRestOfWorld ? '0px 0px 5px #900aff' : 'unset',
        position: 'relative',
      }}
    >
      <CountryPricingContainer container spacing={2}>
        <Grid item xs={12} md={6}>
          <CustomSelect
            id="countryId"
            value={priceObject?.countryId}
            options={filterOtherSelectedCountries(
              unSecureSetupData?.metaCountries.map((country) =>
                country?.id === 'row'
                  ? { ...country, name: 'Global (Rest of the world)' }
                  : country,
              ),
              pricingMatrix,
              priceObject?.countryId,
            )}
            customComponent={StyledEventReactSelect}
            getOptionValue={(option) => option?.id}
            getOptionLabel={(option) => option.name}
            selectType={selectTypes.string}
            menuPlacement="auto"
            label="Country"
            isDisabled={isRestOfWorld}
            onChange={changeCountry}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            menuPortalTarget={document.body}
            invalid={priceObject?.countryId}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomSelect
            id="currencyId"
            value={priceObject?.currencyId}
            options={currency_list?.map((currency) => ({
              ...currency,
              name: `(${currency.symbol}) ${currency.name}`,
            }))}
            customComponent={StyledEventReactSelect}
            getOptionValue={(option) => option?.code}
            getOptionLabel={(option) => option.name}
            selectType={selectTypes.string}
            menuPlacement="auto"
            label="Currency"
            onChangeEvent={handleChange}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            menuPortalTarget={document.body}
            invalid={!priceObject?.currencyId}
            required
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomInput
            id="price"
            value={priceObject?.price}
            label="Price"
            required
            inputAndMessageContainer={StyledEventInputContainer}
            type="number"
            invalid={!priceObject?.price}
            onChange={(e) => {
              const newValue = Math.max(0, e.target.value)
              handleChange({
                target: {
                  value: e.target.value === '' ? '' : newValue,
                  id: 'price',
                  type: 'number',
                },
              })
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomInput
            id="discountedPrice"
            value={priceObject?.discountedPrice}
            label="Discounted Price"
            onFocus={(e) => {
              if (e.target.value === '' || e.target.value === null || e.target.value === undefined) {
                handleChange({
                  target: { value: priceObject?.price, id: 'discountedPrice', type: 'number' },
                })
              }
            }}
            inputAndMessageContainer={StyledEventInputContainer}
            placeholder={priceObject?.price}
            type="number"
            onChange={(e) => {
              const newValue = Math.max(0, e.target.value)
              handleChange({
                target: {
                  value: e.target.value === '' ? '' : newValue,
                  id: 'discountedPrice',
                  type: 'number',
                },
              })
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomInput
            id="introductoryPrice"
            value={priceObject?.introductoryPrice}
            label="Introductory Price"
            onFocus={(e) => {
              if (e.target.value === '' || e.target.value === null || e.target.value === undefined) {
                handleChange({
                  target: { value: priceObject?.discountedPrice || priceObject?.price, id: 'introductoryPrice', type: 'number' },
                })
              }
            }}
            inputAndMessageContainer={StyledEventInputContainer}
            placeholder={priceObject?.discountedPrice || priceObject?.price}
            type="number"
            onChange={(e) => {
              const newValue = Math.max(0, e.target.value)
              handleChange({
                target: {
                  value: e.target.value === '' ? '' : newValue,
                  id: 'introductoryPrice',
                  type: 'number',
                },
              })
            }}
          />
        </Grid>
      </CountryPricingContainer>
      {!isRestOfWorld && (
        <Dialog open={showConfirmation} onClose={() => setShowConfirmation(false)}>
          <DialogTitle>Delete price?</DialogTitle>
          <DialogActions>
            <CancelConfirmationButton onClick={() => setShowConfirmation(false)}>
              Cancel
            </CancelConfirmationButton>
            <ChangesWillNotBeSavedButton onClick={deletePrice}>
              Delete
            </ChangesWillNotBeSavedButton>
          </DialogActions>
        </Dialog>
      )}
      {!isRestOfWorld && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <AddCustomPriceButton color="error" onClick={() => setShowConfirmation(true)}>
            Remove
          </AddCustomPriceButton>
        </Box>
      )}
    </div>
  )
}
